import { call, put, select, takeEvery, takeLatest } from 'typed-redux-saga'
import { authApi, authSlice, AccessCheck, AuthNS, Auth, isChecked, hasAddedProps } from 'Pages/auth'
import { selectHistory } from 'Store'
import { routerActions } from 'connected-react-router'
import {
	LocalStorage,
	modalsSlice,
	notifyMessageCreator,
	Cookie,
	errorHandler,
	ModalUnsignedOrdersComponent,
	PartnerPaymentModal,
	loadersSlice,
	userSettingSlice,
} from 'Modules'
import { FrontUrls, NOT_FOR_REDIRECT_URLS } from 'Src/constants/FrontUrls'
import { MODAL_SIZE, PARAMS_FOR_USER_CHECK } from 'Src/constants/Global'
import { Roles } from 'Src/constants/Roles'
import { MODAL_NAME } from 'Src/constants/ModalName'
import { FormNames } from 'Src/constants/FormNames'
import { UnsignedOrdersRoles, orderApi, UnsignedOrdersModel } from 'Pages/orders_design'
import { interfaceElementsSelector, interfaceElementSlice } from 'Pages/interfaceElement'
import { defaultChannels, socketSlice } from 'Modules/Socket'
import { takeEveryUnique } from 'Helpers'

function* loginSaga(action: AuthNS.Login) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		if (action?.payload?.data?.confirmation_code)
			Cookie.set('confirmation_code', action?.payload?.data?.confirmation_code, {
				'max-age': '86400',
				secure: 'true',
			})
		let authTokens
		if (Cookie.get('confirmation_code')) {
			authTokens = yield* call(authApi.userLogin, {
				...action.payload.data,
				confirmation_code: Cookie.get('confirmation_code'),
			})
		} else {
			authTokens = yield* call(authApi.userLogin, action.payload.data)
		}
		const authLsData = LocalStorage.getUserAuth()
		if (authLsData?.id && authLsData.id !== authTokens.id) yield* call(logoutSaga)
		yield* put(authSlice.actions.afterLogin(authTokens))
	} catch (error) {
		const { errorCallback } = action.payload
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const errorCodes = Object.values(error?.data?.errors[0] ?? []) ?? []
		const isAttemptsBlock = String(errorCodes?.[0] ?? '').includes('attempts_block_for_')
		if (errorCodes.includes('wrong_code') || isAttemptsBlock) {
			Cookie.delete('confirmation_code')
			action?.payload?.callback()
		}
		errorCallback?.(error.data?.errors)
	}
}

function* afterLoginSaga(action: AuthNS.AfterLogin) {
	try {
		const { redirect, isPotentialPartner } = LocalStorage.getUserSettings()
		LocalStorage.setAuth(action.payload)
		LocalStorage.setUserSettings({
			partnerPaymentModalVisible: false,
			viewedServiceNotifications: [],
		})
		yield* put(authSlice.actions.setLogin(action.payload))
		yield* put(authSlice.actions.getUser({ id: action.payload.id, isAfterLogin: true }))
		if (isPotentialPartner) {
			LocalStorage.setUserSettings({ isPotentialPartner: false })
			yield* put(routerActions.push(FrontUrls.prelogin))
		} else if (redirect) {
			yield* put(routerActions.push(redirect))
			LocalStorage.setUserSettings({ redirect: '' })
		} else {
			yield* put(routerActions.push('/'))
		}
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* getUserSaga(action: AuthNS.GetUser) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const user = yield* call(authApi.getUser, action.payload.id)
		yield* put(socketSlice.actions.subscribeChannels(defaultChannels))
		yield* put(authSlice.actions.setUser(user))
		if (action.payload.isAfterLogin) {
			yield* put(authSlice.actions.afterLoginCalls(user))
		}
		const sidebar = yield* select(interfaceElementsSelector.getNavStructure)
		if (!sidebar.length) {
			yield* put(interfaceElementSlice.actions.getInterfaceElementAvailable())
		}
		yield* put(userSettingSlice.actions.getSettings())
		yield* put(loadersSlice.actions.hideGlobalLoader())
		action?.payload?.callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* afterLoginCallsSaga(action: AuthNS.AfterLoginCalls) {
	try {
		yield* call(partnerPaymentModalSaga, action.payload)
		yield* call(unsignedOrdersModalSaga, action.payload)
		yield* put(interfaceElementSlice.actions.getInterfaceElementAvailable())
		const accessCheck = new AccessCheck(action?.payload.roles)
		const { location } = yield* select(selectHistory)
		if (accessCheck.hasRole([Roles.MANAGER]) || accessCheck.hasRole([Roles.HEAD_MANAGER])) {
			if (location.pathname === FrontUrls.root)
				yield* put(routerActions.push(FrontUrls.orders_redesign.index))
		}
		if (accessCheck.hasRole([Roles.CLIENT_IDENTIFIER])) {
			if (location.pathname === FrontUrls.root)
				yield* put(routerActions.push(FrontUrls.customer_identification))
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* logoutSaga(isNeedRedirect = true) {
	try {
		if (LocalStorage.getUserAuth()) {
			const { location } = yield* select(selectHistory)
			if (location && !NOT_FOR_REDIRECT_URLS.has(location.pathname) && isNeedRedirect) {
				LocalStorage.setUserSettings({ redirect: location.pathname })
			}
			LocalStorage.removeAuth()
		}
		yield* put(socketSlice.actions.closeConnection({ isNeedClearChannels: true }))
		yield* put(authSlice.actions.setInitial())
		yield* put(routerActions.push(FrontUrls.auth.index))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* partnerPaymentSaga(action: AuthNS.UpdatePartnerPayment) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(authApi.updatePartnerPayment, action.payload)
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.PARTNER_PAYMENT))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* loginAsSaga(action: AuthNS.LoginAs) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const authTokens = yield* call(authApi.loginAs, action.payload.id)
		yield* call(logoutSaga, false)
		yield* put(authSlice.actions.afterLogin(authTokens))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* resetPasswordSaga(action: AuthNS.ResetPassword) {
	try {
		const { response, email } = action.payload
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(authApi.resetPassword, { email, response })
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* put(authSlice.actions.setChangePassword({ isSuccessChangePassword: true }))
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error, FormNames.AUTH.RESET_PASSWORD_FORM)
	}
}

function* resetPasswordByTokenSaga(action: AuthNS.ResetPasswordByToken) {
	try {
		const { token, password, confirm_password } = action.payload
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(authApi.resetPasswordByToken, { token, password, confirm_password })
		LocalStorage.setAuth(res)
		yield* put(authSlice.actions.setLogin(res))
		yield* put(authSlice.actions.getUser({ id: res.id, isAfterLogin: true }))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'auth.password_change.success')
		yield* put(routerActions.push('/'))
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error, FormNames.AUTH.RESET_PASSWORD_BY_TOKEN)
	}
}

function* partnerPaymentModalSaga(auth: Auth) {
	const checkUser = yield* call(authApi.getCheckUser, auth?.partner_id, PARAMS_FOR_USER_CHECK)

	const accessCheck = new AccessCheck(auth?.roles)
	const isCheck = isChecked(checkUser)
	const showAddedProps = hasAddedProps(auth?.partner?.channels, checkUser)
	const isAdditionalDetails =
		checkUser.kbk === null ||
		checkUser.payment_account === null ||
		checkUser.oktmo === null ||
		checkUser.name_for_payment === null

	const isNeedLater = LocalStorage.getUserSettings().partnerPaymentModalVisible

	if (accessCheck.hasRole([Roles.RUKOVODITEL])) {
		const { location } = yield* select(selectHistory)
		if (location.pathname === FrontUrls.root) yield* put(routerActions.push(FrontUrls.dashboard))
		if (
			(!isNeedLater && isCheck) ||
			(!checkUser.third_party_details && checkUser.budget_organization && isAdditionalDetails) ||
			(checkUser.free_tax_expiration_date &&
				new Date() > new Date(checkUser.free_tax_expiration_date))
		) {
			yield* put(
				modalsSlice.actions.showModal({
					Component: PartnerPaymentModal,
					key: MODAL_NAME.PARTNER_PAYMENT,
					width: '720px',
					title: null,
					componentProps: { item: checkUser, isCheck, showAddedProps },
					closable: false,
					maskClosable: false,
					keyboard: false,
					hideOnlyByUser: true,
				})
			)
		}
	}
	if (accessCheck.hasRole([Roles.POTENTIAL_PARTNER])) {
		const { location } = yield* select(selectHistory)
		if (location.pathname === FrontUrls.root) yield* put(routerActions.push(FrontUrls.prelogin))
	}
}

function* updateUserSaga(action: AuthNS.UpdateUser) {
	try {
		const user = yield* call(authApi.updateUser, action.payload)
		yield* put(authSlice.actions.setUser(user))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* unsignedOrdersModalSaga(auth: Auth) {
	try {
		const { hasRole } = new AccessCheck(auth.roles)
		if (hasRole(UnsignedOrdersRoles)) {
			const { count, unsigned_order_ids }: UnsignedOrdersModel = yield* call(
				orderApi.getUnsignedOrders
			)
			if (count) {
				const isShownWidget = hasRole([Roles.RUKOVODITEL])
				yield* put(
					modalsSlice.actions.showModal({
						Component: ModalUnsignedOrdersComponent,
						key: MODAL_NAME.UNSIGNED_ORDERS,
						width: MODAL_SIZE.S,
						closable: false,
						componentProps: {
							id: auth.id,
							unsigned_order_ids,
							isShownWidget,
						},
					})
				)
			}
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getUserOnlySaga(action: AuthNS.GetUserOnly) {
	try {
		const user = yield* call(authApi.getUser, action.payload.id)
		yield* put(authSlice.actions.setUser(user))
		action?.payload?.callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* refreshPasswordSaga(action: AuthNS.RefreshPassword) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const authTokens = yield* call(authApi.refreshPassword, action.payload)
		yield* put(authSlice.actions.afterLogin(authTokens))
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.AUTH_REFRESH_PASSWORD))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error, FormNames.AUTH.REFRESH_PASSWORD_MODAL)
	}
}

export const authSaga = function* saga() {
	yield* takeLatest(authSlice.actions.updatePartnerPayment, partnerPaymentSaga)
	yield* takeLatest(authSlice.actions.loginAs, loginAsSaga)
	yield* takeEveryUnique(authSlice.actions.resetPassword.type, resetPasswordSaga)
	yield* takeLatest(authSlice.actions.resetPasswordByToken, resetPasswordByTokenSaga)
	yield* takeLatest(authSlice.actions.login, loginSaga)
	yield* takeEvery(authSlice.actions.getUser, getUserSaga)
	yield* takeLatest(authSlice.actions.afterLoginCalls, afterLoginCallsSaga)
	yield* takeLatest(authSlice.actions.logout, logoutSaga)
	yield* takeEvery(authSlice.actions.updateUser, updateUserSaga)
	yield* takeEvery(authSlice.actions.getUserOnly, getUserOnlySaga)
	yield* takeLatest(authSlice.actions.afterLogin, afterLoginSaga)
	yield* takeLatest(authSlice.actions.refreshPassword, refreshPasswordSaga)
}
